import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { deleteDefaultQueryStateFromTable } from 'components/table/utlil';
import {
  closeModal,
  initialQueryState,
  openModal,
  showToastMessage,
  updateQueryStateTable,
  useTableStore,
} from 'enable-ui';

import { useAuthStore, userProfileSelector } from 'modules/auth/core';
import { RoleKey } from 'modules/auth/core/enum';
import { MODAL_KEY } from 'modules/auth/core/modal-key';

import type { AdminUser } from '../type';

import { trimValue } from 'helpers';
import { useNumber, useString } from 'hooks';
import { apiCall } from 'hooks/service';
import { debounce, get, isEmpty, isUndefined } from 'lodash';

import { PATH } from 'constants/path';
import { TABLE_KEY } from 'constants/table';

export const useAdminsController = () => {
  const userProfile = useAuthStore(userProfileSelector);

  const institutionId = userProfile?.institution?.id || '';

  const roleKey = userProfile?.role?.key || '';

  const queryState = useTableStore((s) => s.state.queryState[TABLE_KEY.ADMINS]);

  const navigateTo = useNavigate();

  const total = useNumber(0);

  const [admins, setAdmins] = useState<AdminUser[]>([]);

  const adminIdSelected = useString('');

  const searchValue = useString('');

  useEffect(() => {
    updateQueryStateTable(TABLE_KEY.ADMINS, initialQueryState);

    return () => {
      updateQueryStateTable(TABLE_KEY.ADMINS, {
        ...initialQueryState,
        search: '',
      });
    };
  }, []);

  useEffect(() => {
    if (isEmpty(userProfile)) {
      return;
    }

    if (queryState) {
      getList();
    }
  }, [JSON.stringify(queryState), userProfile]);

  const getList = async () => {
    const result: {
      items: AdminUser[];
      total: number;
    } = await apiCall({
      url: '/users',
      method: 'GET',
      isLoading: true,
      showError: true,
      data: {
        ...deleteDefaultQueryStateFromTable(queryState),
        roleKey:
          roleKey === RoleKey.BIOLOGIC_ADMIN
            ? RoleKey.BIOLOGIC_ADMIN
            : roleKey === RoleKey.INSTITUTION_ADMIN
              ? RoleKey.INSTITUTION_ADMIN
              : '',
        institutionId,
      },
    });

    setAdmins(result?.items ?? []);

    if (!isUndefined(result?.total)) {
      total.setValue(result.total);
    }
  };

  const onClickRow = (row: any) => {
    const original: AdminUser = row?.original;

    if (original?.id) {
      redirectToDetail(original.id);
    }
  };

  const onClickAction = async (key: 'edit' | 'delete' | 'update-status', adminId: string) => {
    switch (key) {
      case 'edit':
        redirectToDetail(adminId);
        break;

      case 'delete':
        adminIdSelected.setValue(adminId);
        openModal(MODAL_KEY.DELETE_ADMIN);
        break;

      case 'update-status':
        await apiCall({
          url: `/users/${adminId}/admin/status`,
          method: 'PATCH',
          isLoading: true,
          showError: true,
        });

        showToastMessage('success', `Updated admin user's status successfully!`);

        getList();
        break;

      default:
        break;
    }
  };

  const redirectToDetail = (id: string) => {
    navigateTo(`${PATH.ADMINS}/${PATH.EDIT_ADMIN.replace(':id', id)}`);
  };

  const onDelete = async () => {
    if (adminIdSelected.value) {
      await apiCall({
        url: `/users/${adminIdSelected.value}/admin`,
        method: 'DELETE',
        isLoading: true,
        showError: true,
      });

      closeModal(MODAL_KEY.DELETE_ADMIN);
      showToastMessage('success', 'Removed admin user successfully!');

      if (queryState.page === 1) {
        getList();

        return;
      }

      updateQueryStateTable(TABLE_KEY.ADMINS, {
        ...queryState,
        page: 1,
      });
    }
  };

  const onChangeSearchValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    searchValue.setValue(event.target.value);
    debounceSearchValue(event.target.value);
  };

  const debounceSearchValue = useCallback(
    debounce((newSearch: string) => {
      updateQueryStateTable(TABLE_KEY.ADMINS, {
        ...queryState,
        page: 1,
        search: trimValue(newSearch),
      });
    }, 500),
    [],
  );

  return {
    total,
    queryState,
    admins,
    onDelete,
    onClickAction,
    onClickRow,
    onChangeSearchValue,
    searchValue,
  };
};
